import { Observable } from './Observable';
import nextTick from 'next-tick';
import ServicesRegistry from './ServicesRegistry';
import { ERROR, LOADING, getModelError, isModelLoading } from './constants';
import debug from 'debug';
const error = debug('core:BaseService:error');
const warn = debug('core:BaseService:warn');
export function disposeAll(subscriptions) {
    while (subscriptions.length) {
        if (subscriptions[0]) {
            subscriptions[0].dispose();
        }
        subscriptions.splice(0, 1);
    }
}
function shallowDiffers(a, b) {
    for (const i in a)
        if (!(i in b))
            return true;
    for (const i in b)
        if (a[i] !== b[i])
            return true;
    return false;
}
class DepsWatcher {
    _deps = {};
    _depsSubscriptions = {};
    _depsModels = {};
    _callback;
    constructor(callback) {
        this._callback = callback;
    }
    addDependency(depId, dep, doImmediateNotify = true) {
        if (this._deps[depId] === dep) {
            //               
            return;
        }
        if (this._deps[depId]) {
            this.removeDependency(depId, false);
        }
        this._deps[depId] = dep;
        this._depsSubscriptions[depId] = dep.subscribeUpdates((model) => {
            this._updateDependencyModel(depId, model);
        });
        this._updateDependencyModel(depId, dep.getModel(), doImmediateNotify);
    }
    removeDependency(depId, doImmediateNotify = true) {
        if (!this._deps[depId]) {
            return;
        }
        this._depsSubscriptions[depId].dispose();
        delete this._depsSubscriptions[depId];
        this._deps[depId].release();
        delete this._deps[depId];
        this._updateDependencyModel(depId, null, doImmediateNotify);
    }
    _updateDependencyModel(depId, model, doNotify = true) {
        const prevDepModels = this._depsModels;
        this._depsModels = { ...this._depsModels }; //              
        if (model != null) {
            this._depsModels[depId] = model;
        }
        else {
            delete this._depsModels[depId];
        }
        if (doNotify) {
            this._callback(this._depsModels, prevDepModels);
        }
    }
    getDepsModels() {
        return this._depsModels;
    }
    dispose() {
        const depIds = Object.keys(this._deps);
        for (const depId of depIds) {
            this.removeDependency(depId, false);
        }
    }
}
export class BaseService extends Observable {
    _model = null;
    _subscriptions = [];
    _depsWatcher;
    _hDependenciesNames; //                      
    _hDepsModels;
    _initializedInstance;
    constructor(initialModel) {
        super();
        ServicesRegistry.register(this, this.constructor.name);
        this._model = initialModel != null ? initialModel : this._createInitialModel();
        //                        
        //               
        //                                
        this._depsWatcher = new DepsWatcher((newModels, prevModels) => {
            this._onDepsUpdated(newModels, prevModels);
        });
        nextTick(() => {
            this._initializedInstance = this;
            if (this._hDepsModels) {
                const prevDepsModels = {};
                Object.keys(this._hDepsModels).forEach(depId => (prevDepsModels[depId] = null));
                this._onDepsUpdated(this._hDepsModels, prevDepsModels);
            }
        });
    }
    /* 
                  
                 
     */
    _createInitialModel() {
        return { [LOADING]: true, [ERROR]: null, loading: true, error: null };
    }
    //                                                  
    _addDependency(depId, dep, doImmediateNotify = true) {
        this._depsWatcher.addDependency(depId, dep, doImmediateNotify);
    }
    _addDependencies(deps, doImmediateNotify = true) {
        const prevDepsModels = this._depsWatcher.getDepsModels();
        for (const depId of Object.keys(deps))
            this._depsWatcher.addDependency(depId, deps[depId], false);
        if (doImmediateNotify)
            this._onDepsUpdated(this._depsWatcher.getDepsModels(), prevDepsModels);
    }
    _removeDependency(depId, doImmediateNotify = true) {
        this._depsWatcher.removeDependency(depId, doImmediateNotify);
    }
    _onDepsUpdated(newModels, prevModels) {
        const depIds = Object.keys(newModels);
        const newDepsModels = depIds.map(depId => newModels[depId]).filter(model => model !== null);
        const errorModel = newDepsModels.find(depModel => !!getModelError(depModel));
        if (errorModel) {
            this._updateWithError(getModelError(errorModel));
            return false;
        }
        const loadingModel = newDepsModels.find(depModel => !!isModelLoading(depModel));
        if (loadingModel) {
            this._updateWithLoading();
            return false;
        }
        this._onDepsReadyAndUpdated(newModels, prevModels);
        return true;
    }
    //              
    _onDepsReadyAndUpdated(newModels, prevModels) {
        //
    }
    _updateWithLoading() {
        if (this._model instanceof Array) {
            this._setModel(Object.assign([...this._model], { [LOADING]: true, [ERROR]: null, loading: true, error: null }));
        }
        else {
            this._updateModel({ [LOADING]: true, [ERROR]: null, loading: true, error: null });
        }
    }
    _updateWithError(error) {
        if (this._model instanceof Array) {
            this._setModel(Object.assign([], { [LOADING]: false, [ERROR]: error, loading: false, error }));
        }
        else {
            this._updateModel({ [LOADING]: false, [ERROR]: error, loading: false, error });
        }
    }
    _updateWithData(partialModel) {
        this._updateModel({ [LOADING]: false, [ERROR]: null, loading: false, error: null, ...partialModel });
    }
    _smartUpdate(newModel, prevModel) {
        return newModel;
    }
    //                                    
    _isModelChanged(nextModel, currentModel) {
        if (currentModel === nextModel)
            return false;
        if (!currentModel && !nextModel)
            return false; //            
        if (!currentModel && nextModel)
            return true; //                         
        if (currentModel && !nextModel)
            return true;
        if (typeof nextModel === 'object' && typeof currentModel === 'object')
            return shallowDiffers(nextModel, currentModel);
        return true;
    }
    _isUpdatesFrozen = false;
    _freezeUpdates(fn) {
        if (this._isUpdatesFrozen) {
            throw new Error('Already frozen');
        }
        const prevModel = this._model;
        this._isUpdatesFrozen = true;
        const unfreeze = () => {
            this._isUpdatesFrozen = false;
            const newModel = this._model;
            this._notifyUpdate(newModel, prevModel);
        };
        let result;
        try {
            result = fn();
            if (result && result.then) {
                //             
                return result
                    .then((res) => {
                    unfreeze();
                    return res;
                })
                    .catch(err => {
                    unfreeze();
                    throw err;
                });
            }
            else {
                unfreeze();
            }
        }
        catch (err) {
            unfreeze();
            throw err;
        }
        return result;
    }
    _notifyUpdate(newModel, prevModel) {
        if (this._isUpdatesFrozen) {
            return;
        }
        this._notify((event) => {
            const subs = Array.isArray(event) ? event : event.split(' ');
            if (subs.includes('update') || subs.includes('*'))
                return true;
            for (const field of subs) {
                if (prevModel[field] !== newModel[field])
                    return true; //                         
            }
            return false;
        }, this._model, prevModel);
        if (this.isReady()) {
            //                                           
            this._notify('ready', this._model);
        }
    }
    _setModel(model) {
        if (!this._isModelChanged(model, this._model)) {
            //                                           
            //                                                     
            return;
        }
        const prevModel = this._model;
        const newModel = this._smartUpdate(model, this._model);
        this._model = newModel;
        this._notifyUpdate(newModel, prevModel);
    }
    _updateModel(partialModel) {
        if (typeof partialModel === 'object') {
            this._setModel({
                ...this._model,
                ...partialModel,
            });
        }
        else {
            //                      
            this._setModel(partialModel);
        }
    }
    /* 
                                               
      
                         
     */
    isReady() {
        if (this._model == null) {
            //          
        }
        return !getModelError(this._model) && !isModelLoading(this._model);
    }
    /* 
                                                                                 
                            
      
                                                          
      
                                
     */
    whenReady() {
        return this.lock(() => {
            if (getModelError(this._model)) {
                return Promise.reject(getModelError(this._model));
            }
            if (this.isReady()) {
                return Promise.resolve(this._model);
            }
            return new Promise((resolve, reject) => {
                if (getModelError(this._model)) {
                    reject(getModelError(this._model));
                    return;
                }
                if (this.isReady()) {
                    resolve(this._model);
                    return;
                }
                const subscription = this.subscribe('update', model => {
                    if (getModelError(model)) {
                        subscription.dispose();
                        reject(getModelError(model));
                    }
                    else if (this.isReady()) {
                        subscription.dispose();
                        resolve(model);
                    }
                });
                //                              
            });
        });
    }
    subscribeAndNotify(event, listener) {
        try {
            listener(this._model);
        }
        catch (err) {
            error(err);
        }
        return this.subscribe(event, listener);
    }
    subscribeUpdates(listener) {
        return this.subscribe('update', listener);
    }
    subscribeUpdatesAndNotify(listener) {
        return this.subscribeAndNotify('update', listener);
    }
    getModel() {
        return this._model;
    }
    //          
    _releaseChild(name) {
        if (!(name in this)) {
            error(`BaseService::_releaseChild: child ${name} not found in`, this);
            throw new Error(`BaseService::_releaseChild: child ${name} not found in ${this}`);
        }
        if (this[name] != null) {
            this[name].release();
            this[name] = null;
        }
    }
    _dispose() {
        this._initializedInstance = false;
        disposeAll(this._subscriptions);
        this._subscriptions = null;
        this._depsWatcher.dispose();
        this._depsWatcher = null;
        //                     
        while (this._hDependenciesNames && this._hDependenciesNames.length) {
            const name = this._hDependenciesNames.pop();
            this[name] = null;
        }
        this._hDepsModels = {};
        this._model = null;
        ServicesRegistry.unregister(this);
        super._dispose();
    }
    static dependency(target, key, descriptor) {
        //                                                                       
        //                                                                 
        //                
        //                             
        //                                    
        //           
        //                                              
        //    
        //
        //                                       
        //                         
        //                                                                   
        //                      
        //      
        //
        //                                          
        //                                         
        //
        //               
        //  
        const depId = key.replace(/^_/, '').replace(/(Service|VC)$/, '');
        if (true /*                        */) {
            //                               
            //                                                            
            if (!('_initializedInstance' in target)) {
                let initializedInstance = false;
                Object.defineProperty(target, '_initializedInstance', {
                    get: () => initializedInstance,
                    set: v => (initializedInstance = v),
                    enumerable: true,
                });
            }
            if (!target._hDependenciesNames)
                target._hDependenciesNames = [];
            if (!target._hDepsModels)
                target._hDepsModels = {};
            target._hDependenciesNames.push(key);
            target._hDepsModels[depId] = null;
            let instance = null;
            const onUpdate = model => {
                const prevDepsModels = { ...target._hDepsModels };
                target._hDepsModels[depId] = model;
                //                                                  
                if (target._initializedInstance) {
                    target._initializedInstance._onDepsUpdated(target._hDepsModels, prevDepsModels);
                }
            };
            Object.defineProperty(target, key, {
                get: () => instance,
                set: v => {
                    if (instance !== v) {
                        if (instance) {
                            instance.unsubscribe(onUpdate);
                            if (!('getInstance' in instance.constructor))
                                instance.release();
                        }
                        instance = v;
                        if (instance) {
                            instance.subscribeUpdatesAndNotify(onUpdate);
                        }
                        else {
                            onUpdate(null);
                        }
                    }
                    return v;
                },
                enumerable: true,
            });
            return;
        }
        throw new Error('Not implemented');
    }
}
